import { useState } from 'react';
import { Mic, MicOff, Loader2, Play, Trash2, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = window.location.origin;

/**
 * Voice Input Component - Records audio and transcribes to text
 * @param {Function} onTranscript - Callback when transcription is complete
 * @param {boolean} disabled - Whether the component is disabled
 */
export const VoiceInput = ({ onTranscript, disabled = false }) => {
  const [transcribing, setTranscribing] = useState(false);
  const {
    isRecording,
    audioBlob,
    audioUrl,
    error,
    formattedDuration,
    startRecording,
    stopRecording,
    clearRecording
  } = useAudioRecorder();

  const handleTranscribe = async () => {
    if (!audioBlob) return;

    setTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', 'en');

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/voice/transcribe`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success && response.data.text) {
        onTranscript(response.data.text);
        clearRecording();
        toast.success('Voice transcribed successfully!');
      } else {
        toast.error('No text was detected in the recording');
      }
    } catch (err) {
      console.error('Transcription error:', err);
      toast.error(err.response?.data?.detail || 'Failed to transcribe audio');
    } finally {
      setTranscribing(false);
    }
  };

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-500 text-sm">
        <MicOff className="h-4 w-4" />
        <span>{error}</span>
        <Button variant="ghost" size="sm" onClick={clearRecording}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {!audioBlob ? (
        // Recording controls
        <>
          {isRecording ? (
            <>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-full">
                <span className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm text-red-600 font-medium">{formattedDuration}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={stopRecording}
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid="stop-recording-btn"
              >
                <MicOff className="h-4 w-4 mr-1" />
                Stop
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={startRecording}
              disabled={disabled}
              className="text-forest hover:bg-forest/5"
              data-testid="start-recording-btn"
            >
              <Mic className="h-4 w-4 mr-1" />
              Record Voice
            </Button>
          )}
        </>
      ) : (
        // Playback and transcribe controls
        <>
          <audio src={audioUrl} controls className="h-8 max-w-[200px]" />
          
          <Button
            variant="ghost"
            size="sm"
            onClick={clearRecording}
            className="text-red-500 hover:text-red-700"
            data-testid="clear-recording-btn"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          
          <Button
            size="sm"
            onClick={handleTranscribe}
            disabled={transcribing}
            className="bg-forest text-white hover:bg-forest/90"
            data-testid="transcribe-btn"
          >
            {transcribing ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Transcribing...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                Use This
              </>
            )}
          </Button>
        </>
      )}
    </div>
  );
};

export default VoiceInput;
